import { memo, useState } from 'react';

import { Alert, Box, Button, ButtonGroup, Heading, Modal, Text, TextButton } from '@zillow/constellation-10';

import { logError } from '@zg-rentals/log-error';
import { DatarouterError } from '@zg-rentals/web-base';

import { RentalManagerRenterRoleWebApiHandler_delete } from '@zg-rentals/ts-rental-manager-api-client';

import type { Client } from '@zg-rentals/http-client';

import type {
  RentalManagerRenterContactTenantDto,
  RentalManagerRenterRoleDeleteRequestDto,
} from '@zg-rentals/ts-rental-manager-api-client';

export type ModalDeleteTenantProps = {
  client: Client;
  isOpen: boolean;
  renter: RentalManagerRenterContactTenantDto;
  onClose: () => void;
  onPrimaryCTAClick?: () => void;
  onSuccess?: () => void | null;
  onError?: (error: DatarouterError) => void | null;
};

/**
 * @param {ModalDeleteTenantProps} props
 *
 * @param {Client} props.client - The client to use for the query
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {RentalManagerRenterContactTenantDto} props.renter - The tenant to delete
 * @param {() => void} props.onClose - The function to call when the modal is closed
 * @param {() => void} props.onSuccess - The function to call when the tenant is successfully deleted
 * @param {() => void} props.onError - The function to call when the tenant is not successfully deleted
 *
 * @returns {JSX.Element}
 */
const ModalDeleteTenancyPeriod = ({
  client,
  isOpen,
  renter,
  onClose,
  onSuccess,
  onError,
}: ModalDeleteTenantProps): JSX.Element => {
  const [serverError, setServerError] = useState<string | null>(null);

  const handleClose = (open: boolean) => {
    if (!open) {
      onClose?.();
    }
  };

  const handleConfirmClick = () => {
    const postData: RentalManagerRenterRoleDeleteRequestDto = {
      renterRoleIdsToDelete: [renter?.displayTenancy?.renterRoleId],
    };

    setServerError(null);
    deleteTenant(postData);
  };

  const deleteTenant = async (postData: RentalManagerRenterRoleDeleteRequestDto) => {
    try {
      await RentalManagerRenterRoleWebApiHandler_delete(postData, client);

      handleSuccess();
    } catch (error) {
      const displayError = DatarouterError.getDisplayMessage(error);
      const errorMessage = displayError;

      const capitalizedError =
        typeof errorMessage === 'string' && errorMessage.length > 0
          ? errorMessage.charAt(0).toUpperCase() + errorMessage.slice(1)
          : 'An unexpected error occurred';

      setServerError(capitalizedError);

      onError?.(new DatarouterError(capitalizedError, 'DELETE_TENANT_ERROR', 500));

      logError({
        error: new Error(`Error adding tenants: ${capitalizedError}`),
        errorType: 'RenterRoleDeleteError',
        context: 'ModalDeleteTenancyPeriod@deleteTenant',
      });
    }
  };

  const handleSuccess = () => {
    onSuccess?.();
    onClose?.();
  };

  const renderBody = () => {
    return (
      <Box data-testid="ModalDeleteTenancyPeriod">
        {serverError && (
          <>
            <Box
              data-testid="ErrorContainer"
              css={{
                marginBottom: '600',
              }}
            >
              <Alert tone="warning">{serverError}</Alert>
            </Box>
          </>
        )}

        <Text>Removing this tenancy period will affect the tenant&apos;s history.</Text>
      </Box>
    );
  };

  return (
    <>
      <Modal
        open={isOpen}
        size={'md'}
        scrollDocument={false}
        header={<Heading level={2}>Remove this tenancy period?</Heading>}
        body={renderBody()}
        footer={
          <ButtonGroup aria-label="modal actions">
            <Modal.Close>
              <TextButton>Cancel</TextButton>
            </Modal.Close>
            <Button emphasis="filled" tone="brand" onClick={handleConfirmClick}>
              Yes, remove
            </Button>
          </ButtonGroup>
        }
        onOpenChange={handleClose}
      />
    </>
  );
};

export default memo(ModalDeleteTenancyPeriod);
